import { useStoreApi } from '@xyflow/react';
import {
  createContext,
  Dispatch,
  SetStateAction,
  use,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { useToast } from 'src/shared/components/ui/use-toast';
import { MODE_PARAM } from 'src/shared/constants/urlParams.constant';
import { useFlow } from 'src/features/flows/queries/useFlow';
import useFlowLock from 'src/features/flows/queries/utils/useFlowLock';
import useFlowValidator from 'src/features/flows/queries/utils/useFlowValidator';
import { useModels } from 'src/features/models/queries/useModels';
import useQueryParams from 'src/shared/hooks/useQueryParams';
import { FlowIssue } from 'src/features/flows/lib/validators';
import { Model } from 'src/shared/types/model.types';
import { LayoutProps } from 'src/shared/types/util.types';
import { IntegrationId } from 'src/shared/types/integration.types';

export type FlowEditorMode = 'EDIT' | 'READ' | 'REVIEW' | 'MONITOR' | 'INITIALIZING';
export type FlowConstructionStatus = 'CONSTRUCT' | 'CONSTRUCTED' | 'HYDRATE' | 'ERROR' | 'DONE';
export const USER_MODES = ['EDIT', 'MONITOR', 'READ', 'REVIEW'];
export const READONLY_MODES = ['READ', 'REVIEW', 'MONITOR', 'INITIALIZING'];

interface UseFlowContextProps {
  readonly: boolean;
  availableModels: Model[];
  flowIntegrations: string;
  flowEditMode: FlowEditorMode | '';
  constructionStatus: FlowConstructionStatus;
  setFlowEditMode: (mode: FlowEditorMode) => Promise<FlowEditorMode>;
  setConstructionStatus: Dispatch<SetStateAction<FlowConstructionStatus>>;
  flowIssues: FlowIssue[];
  cleanOverrides: (newIntegrations: IntegrationId[]) => void;
  flowId: string;
}

const FlowContext = createContext<UseFlowContextProps>({
  readonly: true,
  availableModels: [],
  flowEditMode: 'READ',
  flowIntegrations: '',
  constructionStatus: 'CONSTRUCT',
  setFlowEditMode: () => {
    throw new Error('Tried accessing flowEditMode before initialization!');
  },
  setConstructionStatus: () => {
    throw new Error('Tried accessing constructionStatus before initialization!');
  },
  flowIssues: [],
  cleanOverrides: () => {
    throw new Error('Tried accessing cleanOverrides before initialization!');
  },
  flowId: '',
});

export function useFlowContext() {
  return useContext(FlowContext);
}

export function FlowContextProvider({ children, params }: LayoutProps<'flowId'>) {
  const { flowId } = use(params);
  const { role } = useOrg();
  const store = useStoreApi();
  const { toast } = useToast();
  const { data: models } = useModels();
  const { data: activeFlow } = useFlow(flowId);
  const { setParam: setEditModeParam } = useQueryParams(MODE_PARAM);
  const [currentEditMode, setFlowEditMode] = useState<FlowEditorMode>('INITIALIZING');
  const [constructionStatus, setConstructionStatus] = useState<FlowConstructionStatus>('CONSTRUCT');

  const { flowIssues, cleanOverrides } = useFlowValidator(activeFlow, constructionStatus);

  // ✅ Memoize flowIntegrations - only change when integrations array actually changes
  // Not when activeFlow gets a new reference with same integrations
  const flowIntegrations = useMemo(() => {
    return activeFlow?.integrations.join(',') || '';
  }, [activeFlow?.integrations]);

  const availableModels = useMemo(() => {
    return (
      models?.filter(
        (model) =>
          model.active &&
          (model.inboxes.all || flowIntegrations.split(',').every((inboxId) => model.inboxes.ids.includes(inboxId)))
      ) || []
    );
  }, [models, flowIntegrations]);

  // Define handleEditModeChange early using a ref pattern to avoid circular dependencies
  const handleEditModeChangeRef = useRef<((mode: FlowEditorMode) => Promise<FlowEditorMode>) | undefined>(undefined);

  const handleEditModeChange = useCallback(
    async (newMode: FlowEditorMode) => {
      if (handleEditModeChangeRef.current) {
        return handleEditModeChangeRef.current(newMode);
      }
      return currentEditMode;
    },
    [currentEditMode]
  );

  const lockSocket = useFlowLock(activeFlow, currentEditMode, handleEditModeChange);

  // Assign the actual implementation to the ref
  handleEditModeChangeRef.current = async (newMode: FlowEditorMode) => {
    if (newMode === currentEditMode || !lockSocket.socketReady) {
      !lockSocket.socketReady && console.warn('Tried to change edit mode while socket was not ready!');
      return currentEditMode;
    }
    if (newMode === 'EDIT' && role === 'READER') {
      newMode = 'READ';
    }

    // setInteractiveState logic inlined here
    if (newMode !== 'EDIT') {
      // if switching from edit to read, release the lock
      if (currentEditMode === 'EDIT') {
        lockSocket.emit('unlock');
      }
      setFlowEditMode(newMode);
      store.setState({
        nodesDraggable: false,
        nodesConnectable: false,
      });
      // if we are not initializing, dump the mode to the url
      if (newMode !== 'INITIALIZING') {
        setEditModeParam(newMode);
      }
      return newMode;
    }

    // if coming from review or monitor, unselect all nodes
    if (['REVIEW', 'MONITOR'].includes(currentEditMode)) {
      store.getState().unselectNodesAndEdges();
    }

    // switching to edit mode requires a lock
    lockSocket.emit('lock');
    if (lockSocket.data && ['AVAILABLE', 'ACTIVE'].includes(lockSocket.data.lock)) {
      setFlowEditMode(newMode);
      store.setState({
        nodesDraggable: true,
        nodesConnectable: true,
      });
      setEditModeParam(newMode);
      return newMode;
    } else {
      setFlowEditMode('READ');
      toast({
        variant: 'warning',
        title: 'Unable to edit automation',
        description: 'Someone else is editing this automation',
        id: 'failed',
      });
      return 'READ';
    }
  };

  const readonly = useMemo(
    () => READONLY_MODES.includes(currentEditMode) || role === 'READER',
    [currentEditMode, role]
  );

  const value: UseFlowContextProps = useMemo(
    () => ({
      availableModels,
      constructionStatus,
      flowIntegrations,
      flowEditMode: currentEditMode,
      readonly,
      setConstructionStatus,
      setFlowEditMode: handleEditModeChange,
      flowIssues,
      cleanOverrides,
      flowId,
    }),
    [
      availableModels,
      constructionStatus,
      flowIntegrations,
      currentEditMode,
      readonly,
      setConstructionStatus,
      handleEditModeChange,
      flowIssues,
      cleanOverrides,
      flowId,
    ]
  );

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
}
